import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  LayoutDashboard, Users, Settings, BarChart3, Shield, KeyRound,
  ChevronLeft, ChevronRight, LogOut, Bell, Search, Menu, CreditCard,
  Wallet, Gamepad2, Moon, Sun, Video, Building2, DollarSign,
  Cpu, Activity, Trophy, Server, Award, Eye, FileText, Star, X, Brain,
} from 'lucide-react';
import { adminService } from '../services/adminService';
import './DashboardLayout.css';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  path: string;
  label: string;
  icon: React.ComponentType<any>;
  roles: string[];
  badge?: number;
}

interface MenuGroup {
  label: string;
  items: MenuItem[];
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, logout, hasAnyRole } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [search, setSearch] = useState('');
  const [pendingKyc, setPendingKyc] = useState(0);

  // Fetch pending KYC queue count for the badge
  const fetchPendingKyc = useCallback(async () => {
    try {
      const data = await adminService.getKycQueue({ limit: 1, offset: 0 });
      setPendingKyc(data?.total ?? 0);
    } catch {
      // Non-critical — badge just won't show
    }
  }, []);

  useEffect(() => {
    fetchPendingKyc();
    const interval = setInterval(fetchPendingKyc, 60_000);
    return () => clearInterval(interval);
  }, [fetchPendingKyc]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    navigate(`/dashboard/users?q=${encodeURIComponent(q)}`);
    setSearch('');
  };

  const menuGroups: MenuGroup[] = [
    {
      label: 'Overview',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin'] },
      ],
    },
    {
      label: 'Operations',
      items: [
        { path: '/dashboard/users', label: 'Users', icon: Users, roles: ['admin'] },
        {
          path: '/dashboard/compliance',
          label: 'KYC & Compliance',
          icon: Award,
          roles: ['admin'],
          badge: pendingKyc > 0 ? pendingKyc : undefined,
        },
        { path: '/dashboard/games', label: 'Protocol Sessions', icon: Gamepad2, roles: ['admin'] },
        { path: '/dashboard/pro', label: 'Pro Workspace', icon: Brain, roles: ['admin'] },
        { path: '/dashboard/audit', label: 'Audit Logs', icon: Activity, roles: ['admin'] },
        { path: '/dashboard/health', label: 'System Health', icon: Eye, roles: ['admin'] },
        { path: '/dashboard/roles', label: 'Roles', icon: KeyRound, roles: ['admin'] },
      ],
    },
    {
      label: 'Money',
      items: [
        { path: '/dashboard/withdrawals', label: 'Withdrawals', icon: Wallet, roles: ['admin'] },
        { path: '/dashboard/transactions', label: 'Transactions', icon: CreditCard, roles: ['admin'] },
        { path: '/dashboard/settlement', label: 'Settlement', icon: DollarSign, roles: ['admin'] },
        { path: '/dashboard/providers', label: 'Payment Providers', icon: Server, roles: ['admin'] },
        { path: '/dashboard/banks', label: 'Banks', icon: Building2, roles: ['admin'] },
      ],
    },
    {
      label: 'Growth',
      items: [
        { path: '/dashboard/campaigns', label: 'Campaigns', icon: Video, roles: ['admin'] },
        { path: '/dashboard/tasks', label: 'Tasks / Offerwall', icon: Cpu, roles: ['admin'] },
        { path: '/dashboard/leaderboard', label: 'Leaderboard', icon: Trophy, roles: ['admin'] },
      ],
    },
    {
      label: 'Trust & Safety',
      items: [
        { path: '/dashboard/fraud', label: 'Fraud Detection', icon: Shield, roles: ['admin'] },
        { path: '/dashboard/biokinetic', label: 'Bio-Kinetic', icon: Shield, roles: ['admin'] },
      ],
    },
    {
      label: 'Storytelling',
      items: [
        { path: '/dashboard/brief', label: 'Visa Brief', icon: FileText, roles: ['admin'] },
        { path: '/dashboard/press', label: 'Press & Accolades', icon: Star, roles: ['admin'] },
      ],
    },
    {
      label: 'Platform',
      items: [
        { path: '/dashboard/config', label: 'Configuration', icon: Settings, roles: ['admin'] },
        { path: '/dashboard/metrics', label: 'Metrics', icon: BarChart3, roles: ['admin'] },
      ],
    },
  ];

  const filteredGroups = menuGroups
    .map((g) => ({ ...g, items: g.items.filter((i) => hasAnyRole(i.roles)) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className={`dashboard-container ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="trinze-logo">
            <div className="trinze-logo-icon">T</div>
            {sidebarOpen && <span className="trinze-logo-text">trinze</span>}
          </div>
          {sidebarOpen && (
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(false)}
              aria-label="Collapse sidebar"
            >
              <ChevronLeft size={16} />
            </button>
          )}
        </div>

        {!sidebarOpen && (
          <button
            className="sidebar-expand-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Expand sidebar"
          >
            <ChevronRight size={16} />
          </button>
        )}

        <nav className="sidebar-nav">
          {filteredGroups.map((group) => (
            <div key={group.label} className="nav-group">
              {sidebarOpen && (
                <span className="nav-group-label">{group.label}</span>
              )}
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.path ||
                  (item.path !== '/dashboard' &&
                    location.pathname.startsWith(item.path));

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${isActive ? 'active' : ''}`}
                    title={!sidebarOpen ? item.label : undefined}
                  >
                    <Icon size={18} className="nav-icon" />
                    {sidebarOpen && (
                      <span className="nav-label">{item.label}</span>
                    )}
                    {item.badge !== undefined && (
                      <span className={`nav-badge ${isActive ? 'active' : ''}`}>
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          {sidebarOpen && (
            <div className="sidebar-user">
              <div className="sidebar-user-avatar">
                {user?.full_name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">{user?.full_name || 'Admin'}</span>
                <span className="sidebar-user-role">{user?.role || 'Administrator'}</span>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="nav-item logout-item"
            title={!sidebarOpen ? 'Logout' : undefined}
          >
            <LogOut size={18} className="nav-icon" />
            {sidebarOpen && <span className="nav-label">Logout</span>}
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button
              className="mobile-menu-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle navigation"
            >
              <Menu size={22} />
            </button>
            <form className="search-bar" onSubmit={handleSearch}>
              <Search size={16} />
              <input
                type="text"
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="search-clear"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </form>
          </div>
          <div className="header-right">
            <button
              className="theme-toggle-btn"
              onClick={toggleTheme}
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            {pendingKyc > 0 && (
              <button
                className="notification-btn"
                onClick={() => navigate('/dashboard/compliance')}
                title={`${pendingKyc} pending KYC submission${pendingKyc !== 1 ? 's' : ''}`}
              >
                <Bell size={18} />
                <span className="notification-badge">
                  {pendingKyc > 9 ? '9+' : pendingKyc}
                </span>
              </button>
            )}
            <div className="user-chip">
              <div className="user-avatar">
                {user?.full_name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <div className="user-info">
                <span className="user-name">{user?.full_name || 'Admin'}</span>
                <span className="user-role">{user?.role || 'Administrator'}</span>
              </div>
            </div>
          </div>
        </header>

        <div className="content-area">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
